import React, { useMemo } from 'react';
import { ChartAreaSkeleton } from '../common/loading';

import {
  Box,
  Paper,
  Typography,
  Button,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import { FilterList as FilterListIcon, SelectAll as SelectAllIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { SEARCE_COLORS } from '../../theme';
import { DRI_LINE_CHART_HEIGHT } from '../../constants/driInsightsConstants';

export interface WeeklyDriProduct {
  name: string;
  averageDri: number;
}

export interface WeeklyDriDataPoint {
  week: string;
  [productName: string]: string | number;
}

export interface WeeklyDriDataAll {
  data: WeeklyDriDataPoint[];
  products: WeeklyDriProduct[];
}

export interface WeeklyDriDataFiltered {
  data: WeeklyDriDataPoint[];
  products: string[];
}

export interface WeeklyTrendTooltipState {
  x: number;
  y: number;
  week: string;
  entries: Array<{ name: string; value: number; color: string }>;
}

/** Raw row from weekly trends API (week_start_date, product_name, average_dri_score). */
export type WeeklyTrendRow = { week_start_date?: string; product_name?: string; average_dri_score?: number };

const DEFAULT_TOP_N_PRODUCTS = 5;

/**
 * Product names with the highest average DRI on the latest week that has data (ties broken by name).
 * If the chronologically latest week has no rows, walks backward to the previous week.
 */
export function getTopNProductsByLatestWeek(rows: WeeklyTrendRow[], n: number = DEFAULT_TOP_N_PRODUCTS): string[] {
  if (!rows?.length || n <= 0) return [];
  const weekSet = new Set<string>();
  rows.forEach((r) => {
    if (r.week_start_date) weekSet.add(r.week_start_date);
  });
  const sortedWeeks = Array.from(weekSet).sort();
  for (let i = sortedWeeks.length - 1; i >= 0; i--) {
    const week = sortedWeeks[i];
    const inWeek = rows.filter((r) => r.week_start_date === week && r.product_name);
    if (inWeek.length === 0) continue;
    const byProduct = new Map<string, number>();
    inWeek.forEach((r) => {
      const name = r.product_name as string;
      const score = r.average_dri_score ?? 0;
      byProduct.set(name, Math.max(byProduct.get(name) ?? score, score));
    });
    const ranked = Array.from(byProduct.entries())
      .map(([name, score]) => ({ name, score }))
      .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
    return ranked.slice(0, n).map((x) => x.name);
  }
  return [];
}

export interface WeeklyDRITrendsChartProps {
  /** Raw weekly trends from API (stays in DRIInsights; chart derives display data). */
  weeklyTrendsRaw: WeeklyTrendRow[];
  /** Available product names from API (for BU/SBU filter context). */
  availableProductsFromApi: string[];
  isLoading: boolean;
  selectedProducts: string[];
  onSelectedProductsChange: (products: string[]) => void;
  pendingProducts: string[];
  onPendingProductsChange: (products: string[]) => void;
  productFilterMenuAnchor: HTMLElement | null;
  onProductFilterMenuAnchorChange: (el: HTMLElement | null) => void;
  trendChartStartDate: Date | null;
  trendChartEndDate: Date | null;
  onTrendChartStartDateChange: (d: Date | null) => void;
  onTrendChartEndDateChange: (d: Date | null) => void;
  dateRangeMenuAnchor: HTMLElement | null;
  onDateRangeMenuAnchorChange: (el: HTMLElement | null) => void;
  dateRangeTab: number;
  onDateRangeTabChange: (tab: number) => void;
  hoveredDataPoints: Set<string>;
  onHoveredDataPointsChange: (set: Set<string>) => void;
  tooltipState: WeeklyTrendTooltipState | null;
  onTooltipStateChange: (state: WeeklyTrendTooltipState | null) => void;
  hasBeenManuallyClearedRef: React.MutableRefObject<boolean>;
}

const LINE_CHART_COLORS = [
  SEARCE_COLORS.primary,
  SEARCE_COLORS.success,
  SEARCE_COLORS.warning,
  SEARCE_COLORS.error,
  '#9C27B0',
  '#FF5722',
  '#00BCD4',
  '#FF9800',
  '#4CAF50',
  '#2196F3',
];

/**
 * Build chart data and product list from raw weekly trends API data.
 */
function computeWeeklyDriDataAll(
  weeklyTrendsRaw: WeeklyTrendRow[],
  availableProductsFromApi: string[]
): WeeklyDriDataAll {
  if (!weeklyTrendsRaw?.length) {
    return {
      data: [],
      products: availableProductsFromApi.map((name) => ({ name, averageDri: 0 })),
    };
  }
  const weekProductMap = new Map<string, Map<string, number>>();
  const productDriTotals = new Map<string, { total: number; count: number }>();

  weeklyTrendsRaw.forEach((row) => {
    if (!row.week_start_date || !row.product_name) return;
    const weekKey = row.week_start_date;
    const productName = row.product_name;
    const averageDriScore = row.average_dri_score ?? 0;

    const stats = productDriTotals.get(productName) ?? { total: 0, count: 0 };
    stats.total += averageDriScore;
    stats.count += 1;
    productDriTotals.set(productName, stats);

    if (!weekProductMap.has(weekKey)) weekProductMap.set(weekKey, new Map());
    weekProductMap.get(weekKey)!.set(productName, averageDriScore);
  });

  const weeks = Array.from(weekProductMap.keys()).sort();
  const allProductsSet = new Set<string>(availableProductsFromApi);
  productDriTotals.forEach((_, productName) => allProductsSet.add(productName));

  const chartData = weeks.map((week) => {
    const weekDate = new Date(week);
    const weekLabel = `${weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    const dataPoint: Record<string, unknown> = {
      week: weekLabel,
      weekKey: week,
      date: weekDate,
    };
    allProductsSet.forEach((productName) => {
      const averageDri = weekProductMap.get(week)?.get(productName);
      dataPoint[productName] = averageDri !== undefined ? parseFloat((averageDri as number).toFixed(2)) : null;
    });
    return dataPoint as WeeklyDriDataPoint;
  });

  const products = Array.from(allProductsSet)
    .map((name) => {
      const stats = productDriTotals.get(name);
      return {
        name,
        averageDri: stats?.count ? parseFloat((stats.total / stats.count).toFixed(2)) : 0,
      };
    })
    .sort((a, b) => b.averageDri - a.averageDri);

  return { data: chartData, products };
}

/**
 * Filter chart data by selected products and date range.
 */
function computeWeeklyDriData(
  weeklyDriDataAll: WeeklyDriDataAll,
  selectedProducts: string[],
  trendChartStartDate: Date | null,
  trendChartEndDate: Date | null
): WeeklyDriDataFiltered {
  if (!selectedProducts?.length) return { data: [], products: [] };

  let dateFilteredData = weeklyDriDataAll.data;
  if (trendChartStartDate || trendChartEndDate) {
    dateFilteredData = weeklyDriDataAll.data.filter((weekData) => {
      const rawDate = (weekData as { date?: Date | string }).date;
      if (!rawDate) return false;
      const weekDate = typeof rawDate === 'string' ? parseISO(rawDate) : rawDate;
      const weekStart = startOfWeek(weekDate, { weekStartsOn: 1 });
      if (trendChartStartDate && weekStart < trendChartStartDate) return false;
      if (trendChartEndDate) {
        const weekEnd = endOfWeek(weekDate, { weekStartsOn: 1 });
        if (weekEnd > trendChartEndDate) return false;
      }
      return true;
    });
  }

  const filteredData = dateFilteredData.map((weekData) => {
    const filteredWeekData: Record<string, unknown> = {
      week: (weekData as { week?: string }).week,
      weekKey: (weekData as { weekKey?: string }).weekKey,
      date: (weekData as { date?: Date }).date,
    };
    selectedProducts.forEach((product) => {
      if ((weekData as Record<string, unknown>)[product] !== undefined) {
        filteredWeekData[product] = (weekData as Record<string, unknown>)[product];
      }
    });
    return filteredWeekData as WeeklyDriDataPoint;
  });

  const products = selectedProducts
    .filter((product) => weeklyDriDataAll.products.some((p) => p.name === product))
    .sort();

  return { data: filteredData, products };
}

/**
 * Weekly DRI Trends by Product – line chart with product and date range filters.
 * Derives chart data from raw API data (weeklyTrendsRaw, availableProductsFromApi).
 */
const WeeklyDRITrendsChart: React.FC<WeeklyDRITrendsChartProps> = ({
  weeklyTrendsRaw,
  availableProductsFromApi,
  isLoading,
  selectedProducts,
  onSelectedProductsChange,
  pendingProducts,
  onPendingProductsChange,
  productFilterMenuAnchor,
  onProductFilterMenuAnchorChange,
  trendChartStartDate,
  trendChartEndDate,
  onTrendChartStartDateChange,
  onTrendChartEndDateChange,
  dateRangeMenuAnchor,
  onDateRangeMenuAnchorChange,
  dateRangeTab,
  onDateRangeTabChange,
  hoveredDataPoints,
  onHoveredDataPointsChange,
  tooltipState,
  onTooltipStateChange,
  hasBeenManuallyClearedRef,
}) => {
  const weeklyDriDataAll = useMemo(
    () => computeWeeklyDriDataAll(weeklyTrendsRaw, availableProductsFromApi),
    [weeklyTrendsRaw, availableProductsFromApi]
  );
  const weeklyDriData = useMemo(
    () => computeWeeklyDriData(weeklyDriDataAll, selectedProducts, trendChartStartDate, trendChartEndDate),
    [weeklyDriDataAll, selectedProducts, trendChartStartDate, trendChartEndDate]
  );
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid ${SEARCE_COLORS.neutral_200}`,
        borderRadius: 2,
        backgroundColor: SEARCE_COLORS.white,
      }}
    >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, flexShrink: 0 }}>
              Weekly DRI Trends by Product
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
              {weeklyDriDataAll.products.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    onPendingProductsChange(selectedProducts);
                    onProductFilterMenuAnchorChange(e.currentTarget);
                  }}
                  sx={{
                    minWidth: 140,
                    maxWidth: 160,
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    height: '32px',
                    fontSize: '0.75rem',
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'calc(100% - 24px)' }}>
                    {selectedProducts.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        All products
                      </Typography>
                    ) : selectedProducts.length === weeklyDriDataAll.products.length ? (
                      <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        All products
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedProducts.length} selected
                      </Typography>
                    )}
                  </Box>
                  <FilterListIcon fontSize="small" sx={{ ml: 0.5, flexShrink: 0 }} />
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => onDateRangeMenuAnchorChange(e.currentTarget)}
                sx={{
                  minWidth: 140,
                  maxWidth: 160,
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  height: '32px',
                  fontSize: '0.75rem',
                }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'calc(100% - 24px)' }}>
                  <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {trendChartStartDate && trendChartEndDate
                      ? `${format(trendChartStartDate, 'MMM d')} - ${format(trendChartEndDate, 'MMM d')}`
                      : 'Date Range'}
                  </Typography>
                </Box>
                <FilterListIcon fontSize="small" sx={{ ml: 0.5, flexShrink: 0 }} />
              </Button>
              <Menu
                anchorEl={dateRangeMenuAnchor}
                open={Boolean(dateRangeMenuAnchor)}
                onClose={() => onDateRangeMenuAnchorChange(null)}
                PaperProps={{
                  sx: { minWidth: 350, maxWidth: 400 },
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Date Range
                  </Typography>
                  <Tabs
                    value={dateRangeTab}
                    onChange={(_, newValue) => onDateRangeTabChange(newValue)}
                    sx={{
                      mb: 2,
                      minHeight: 'auto',
                      '& .MuiTab-root': {
                        minHeight: 'auto',
                        fontSize: '0.75rem',
                        padding: '8px 16px',
                        textTransform: 'none',
                      },
                    }}
                  >
                    <Tab label="Start Date" />
                    <Tab label="End Date" />
                  </Tabs>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    {dateRangeTab === 0 ? (
                      <DatePicker
                        label="Start Date"
                        value={trendChartStartDate}
                        onChange={(newValue) => {
                          if (newValue) {
                            onTrendChartStartDateChange(startOfWeek(newValue, { weekStartsOn: 1 }));
                          } else {
                            onTrendChartStartDateChange(null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
                            sx: {
                              '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                              '& .MuiInputBase-input': { fontSize: '0.875rem' },
                            },
                          },
                        }}
                      />
                    ) : (
                      <DatePicker
                        label="End Date"
                        value={trendChartEndDate}
                        onChange={(newValue) => {
                          if (newValue) {
                            onTrendChartEndDateChange(endOfWeek(newValue, { weekStartsOn: 1 }));
                          } else {
                            onTrendChartEndDateChange(null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
                            sx: {
                              '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                              '& .MuiInputBase-input': { fontSize: '0.875rem' },
                            },
                          },
                        }}
                      />
                    )}
                  </LocalizationProvider>
                </Box>
              </Menu>
            </Box>
          </Box>

          {weeklyDriDataAll.products.length > 0 && (
            <>
              <Menu
                anchorEl={productFilterMenuAnchor}
                open={Boolean(productFilterMenuAnchor)}
                onClose={() => {
                  onPendingProductsChange(selectedProducts);
                  onProductFilterMenuAnchorChange(null);
                }}
                PaperProps={{
                  sx: { maxHeight: 400, minWidth: 300, maxWidth: 500 },
                }}
              >
                <MenuItem
                  sx={{
                    '&:hover': { backgroundColor: 'transparent' },
                    cursor: 'default',
                    padding: '8px 16px',
                  }}
                  disableRipple
                >
                  <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <Button
                      size="small"
                      startIcon={<SelectAllIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPendingProductsChange(weeklyDriDataAll.products.map((p) => p.name));
                      }}
                      sx={{
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        flex: 1,
                        textTransform: 'none',
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPendingProductsChange([]);
                      }}
                      sx={{
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        flex: 1,
                        textTransform: 'none',
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                </MenuItem>
                <Divider />
                {weeklyDriDataAll.products.map((productObj) => (
                  <MenuItem
                    key={productObj.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      const newSelection = pendingProducts.includes(productObj.name)
                        ? pendingProducts.filter((p) => p !== productObj.name)
                        : [...pendingProducts, productObj.name];
                      onPendingProductsChange(newSelection);
                    }}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <Checkbox
                        checked={pendingProducts.includes(productObj.name)}
                        sx={{
                          padding: '4px',
                          mr: 1,
                          '& .MuiSvgIcon-root': { fontSize: '1rem' },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {productObj.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        ml: 2,
                        color: SEARCE_COLORS.textSecondary,
                        fontWeight: 600,
                        backgroundColor: alpha(SEARCE_COLORS.neutral_100, 0.5),
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.65rem',
                        flexShrink: 0,
                      }}
                    >
                      {productObj.averageDri.toFixed(2)}
                    </Typography>
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem
                  sx={{
                    '&:hover': { backgroundColor: 'transparent' },
                    cursor: 'default',
                    padding: '8px 16px',
                  }}
                  disableRipple
                >
                  <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPendingProductsChange(selectedProducts);
                        onProductFilterMenuAnchorChange(null);
                      }}
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        color: SEARCE_COLORS.textSecondary,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pendingProducts.length === 0) {
                          hasBeenManuallyClearedRef.current = true;
                        } else {
                          hasBeenManuallyClearedRef.current = false;
                        }
                        onSelectedProductsChange(pendingProducts);
                        onProductFilterMenuAnchorChange(null);
                      }}
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        backgroundColor: SEARCE_COLORS.primary,
                        '&:hover': { backgroundColor: SEARCE_COLORS.primary },
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </MenuItem>
              </Menu>
            </>
          )}

          <Box
            sx={{
              height: DRI_LINE_CHART_HEIGHT - 40,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
            onMouseLeave={() => {
              onHoveredDataPointsChange(new Set());
              onTooltipStateChange(null);
            }}
          >
            {isLoading ? (
              <Box sx={{ height: DRI_LINE_CHART_HEIGHT, py: 1 }}>
                <ChartAreaSkeleton titleWidth="50%" height={DRI_LINE_CHART_HEIGHT - 48} />
              </Box>
            ) : weeklyDriData.data.length > 0 && weeklyDriData.products.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={DRI_LINE_CHART_HEIGHT}>
                  <LineChart
                    data={weeklyDriData.data}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={alpha(SEARCE_COLORS.neutral_300, 0.5)}
                    />
                    <XAxis
                      dataKey="week"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fill: SEARCE_COLORS.textSecondary, fontSize: 11 }}
                      interval={0}                    />
                    <YAxis
                      tick={{ fill: SEARCE_COLORS.textSecondary, fontSize: 12 }}
                      label={{
                        value: 'Average DRI Score',
                        angle: -90,
                        position: 'insideLeft',
                        style: { fill: SEARCE_COLORS.textSecondary, fontSize: 12 },
                      }}
                    />
                    <RechartsTooltip
                      active={tooltipState !== null}
                      shared={false}
                      allowEscapeViewBox={{ x: false, y: false }}
                      payload={
                        tooltipState
                          ? tooltipState.entries.map((entry) => ({
                              name: entry.name,
                              value: entry.value,
                              color: entry.color,
                              dataKey: entry.name,
                            }))
                          : []
                      }
                      label={tooltipState?.week}
                      coordinate={tooltipState ? { x: tooltipState.x, y: tooltipState.y } : undefined}
                      content={({ active }) => {
                        if (!active || !tooltipState?.entries?.length) return null;
                        const entry = tooltipState.entries[0];
                        if (!entry || entry.value == null) return null;
                        return (
                          <Box
                            sx={{
                              backgroundColor: SEARCE_COLORS.white,
                              border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                              borderRadius: 1,
                              p: 1.5,
                              boxShadow: `0 2px 8px ${alpha(SEARCE_COLORS.primary, 0.15)}`,
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {tooltipState.week}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5, color: SEARCE_COLORS.textSecondary }}>
                              {entry.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: entry.color, fontWeight: 600 }}>
                              {entry.value.toFixed(2)}
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '5px', paddingBottom: '0px', marginBottom: '0px', fontSize: '0.75rem' }}
                      iconType="line"
                      formatter={(value) => <span style={{ fontSize: '0.75rem' }}>{value}</span>}
                    />
                    {weeklyDriData.products.map((product, index) => {
                      const color = LINE_CHART_COLORS[index % LINE_CHART_COLORS.length];
                      const createCustomDot = (productName: string, dotColor: string) => {
                        return (props: { cx: number | null; cy: number | null; payload: WeeklyDriDataPoint }): React.ReactElement => {
                          const { cx, cy, payload } = props;
                          if (cx == null || cy == null || payload == null || payload[productName] == null) {
                            return <g />;
                          }
                          const dataPointKey = `${productName}-${payload.week}`;
                          const isHovered = hoveredDataPoints.has(dataPointKey);
                          const dotRadius = 4;
                          const value = Number(payload[productName]);
                          return (
                            <g>
                              <circle
                                cx={cx}
                                cy={cy}
                                r={dotRadius}
                                fill={dotColor}
                                stroke="none"
                                strokeWidth={0}
                                style={{
                                  pointerEvents: 'all',
                                  cursor: 'pointer',
                                  shapeRendering: 'crispEdges',
                                }}
                                onMouseEnter={() => {
                                  onHoveredDataPointsChange(new Set([dataPointKey]));
                                  onTooltipStateChange({
                                    x: cx,
                                    y: cy,
                                    week: payload.week,
                                    entries: [{ name: productName, value, color: dotColor }],
                                  });
                                }}
                                onMouseLeave={() => {
                                  const newSet = new Set(hoveredDataPoints);
                                  newSet.delete(dataPointKey);
                                  onHoveredDataPointsChange(newSet);
                                  onTooltipStateChange(null);
                                }}
                              />
                              {isHovered && (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={6}
                                  fill={dotColor}
                                  stroke={SEARCE_COLORS.white}
                                  strokeWidth={2}
                                  style={{
                                    pointerEvents: 'none',
                                    transition: 'all 0.2s ease',
                                  }}
                                />
                              )}
                            </g>
                          );
                        };
                      };
                      const CustomDot = createCustomDot(product, color);
                      return (
                        <Line
                          key={product}
                          type="monotone"
                          dataKey={product}
                          stroke={color}
                          strokeWidth={2}
                          dot={CustomDot}
                          activeDot={false}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: DRI_LINE_CHART_HEIGHT,
                  gap: 2,
                  px: 2,
                  textAlign: 'center',
                }}
              >
                {weeklyDriDataAll.products.length === 0 && weeklyDriDataAll.data.length === 0 ? (
                  <>
                    <Typography variant="h6" sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 500 }}>
                      No weekly trend data
                    </Typography>
                    <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
                      There is no weekly DRI trend data for the current filters, or the report has not been populated yet.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 500 }}>
                      No products selected
                    </Typography>
                    <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
                      Please select one or more products from the filter above to view the trend chart
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
    </Paper>
  );
};

export default WeeklyDRITrendsChart;
